import React from 'react';
import { useIntl } from 'react-intl';
import Box from '@amzn/meridian/box';
import Column from '@amzn/meridian/column';
import Row from '@amzn/meridian/row';
import Button from '@amzn/meridian/button';
import Heading from '@amzn/meridian/heading';
import Icon from '@amzn/meridian/icon';
import Select, { SelectOption } from '@amzn/meridian/select';
import closeTokens from '@amzn/meridian-tokens/base/icon/close-large';
import { Ownership } from './PastContactsTypes';
import {
  CHANNEL_OPTIONS,
  DURATION_OPTIONS,
  OWNERSHIP_OPTIONS,
  TIME_OF_DAY_OPTIONS,
} from './utils/PastContactsConstants';

interface PastContactsFiltersProps {
  selectedChannel: string;
  onChannelChange: (channel: string) => void;
  selectedTimeOfDay: number;
  onTimeOfDayChange: (timeOfDay: number) => void;
  selectedDuration: number;
  onDurationChange: (duration: number) => void;
  selectedOwnership: string;
  onOwnershipChange: (ownership: Ownership) => void;
  selectedWeek: string;
  onWeekChange: (week: string) => void;
  weekOptions: string[];
  onResetFilters: () => void;
  onClose: () => void;
}

export const PastContactsFilters: React.FC<PastContactsFiltersProps> = ({
  selectedChannel,
  onChannelChange,
  selectedTimeOfDay,
  onTimeOfDayChange,
  selectedDuration,
  onDurationChange,
  selectedOwnership,
  onOwnershipChange,
  selectedWeek,
  weekOptions,
  onWeekChange,
  onResetFilters,
  onClose,
}) => {
  const intl = useIntl();

  const hasActiveFilters =
    selectedChannel !== 'All' ||
    selectedTimeOfDay != 0 ||
    selectedDuration != 0 ||
    selectedOwnership != Ownership.ALL;

  return (
    <Box spacingInset='none 300 none 300'>
      <Row
        alignmentVertical='center'
        alignmentHorizontal='end'
        spacingInset='500 none 500'
      >
        <Button
          type='icon'
          onClick={onClose}
          aria-label={intl.formatMessage({
            id: 'cs_perfportal_past_contacts_filters_close',
            defaultMessage: 'Close filters',
          })}
        >
          <Icon tokens={closeTokens} />
        </Button>
      </Row>
      <Row
        alignmentVertical='center'
        alignmentHorizontal='justify'
        spacingInset='500 none 400'
      >
        <Heading level={6}>
          {intl.formatMessage({
            id: 'cs_perfportal_past_contacts_filters_title',
            defaultMessage: 'Filter by',
          })}
        </Heading>
      </Row>

      <Column spacingInset='400 none none none'>
        <Row spacingInset='100 none 100'>
          <Select
            label={intl.formatMessage({
              id: 'cs_perfportal_week',
              defaultMessage: 'Week',
            })}
            value={selectedWeek}
            onChange={onWeekChange}
            width={250}
          >
            {weekOptions.map(week => (
              <SelectOption key={week} value={week} label={week} />
            ))}
          </Select>
        </Row>
        <Row spacingInset='100 none 100'>
          <Select
            label={intl.formatMessage({
              id: 'cs_perfportal_past_contacts_filters_time_of_day',
              defaultMessage: 'Time of day',
            })}
            value={selectedTimeOfDay}
            onChange={onTimeOfDayChange}
            width={250}
          >
            {Object.values(TIME_OF_DAY_OPTIONS).map(timeOption => (
              <SelectOption
                key={timeOption.value}
                value={timeOption.value}
                label={intl.formatMessage({
                  id: timeOption.translationId,
                  defaultMessage: timeOption.defaultMessage,
                })}
              />
            ))}
          </Select>
        </Row>
        <Row spacingInset='100 none 100'>
          <Select
            label={intl.formatMessage({
              id: 'cs_perfportal_channel',
              defaultMessage: 'Channel',
            })}
            value={selectedChannel}
            onChange={onChannelChange}
            width={250}
          >
            {CHANNEL_OPTIONS.map(channel => (
              <SelectOption
                key={channel.value}
                value={channel.value}
                label={intl.formatMessage({
                  id: channel.translationId,
                  defaultMessage: channel.defaultMessage,
                })}
              />
            ))}
          </Select>
        </Row>
        <Row spacingInset='100 none 100'>
          <Select
            label={intl.formatMessage({
              id: 'cs_perfportal_duration',
              defaultMessage: 'Duration',
            })}
            value={selectedDuration}
            onChange={onDurationChange}
            width={250}
          >
            {Object.values(DURATION_OPTIONS).map(duration => (
              <SelectOption
                key={duration.value}
                value={duration.value}
                label={intl.formatMessage({
                  id: duration.translationId,
                  defaultMessage: duration.defaultMessage,
                })}
              />
            ))}
          </Select>
        </Row>
        <Row spacingInset='100 none 100'>
          <Select
            label={intl.formatMessage({
              id: 'cs_perfportal_ownership',
              defaultMessage: 'Ownership',
            })}
            value={selectedOwnership}
            onChange={onOwnershipChange}
            width={250}
          >
            {OWNERSHIP_OPTIONS.map(ownership => (
              <SelectOption
                key={ownership.value}
                value={ownership.value}
                label={intl.formatMessage({
                  id: ownership.translationId,
                  defaultMessage: ownership.defaultMessage,
                })}
              />
            ))}
          </Select>
        </Row>
      </Column>

      {hasActiveFilters && (
        <Column spacing='200'>
          <Button type='link' onClick={onResetFilters}>
            {intl.formatMessage({
              id: 'cs_perfportal_past_contacts_filters_reset',
              defaultMessage: 'Reset Filters',
            })}
          </Button>
        </Column>
      )}
    </Box>
  );
};

export default PastContactsFilters;
